import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  isSelectableForMonitoringSelectionMode,
  isUnitSelectableForAIPanel,
} from 'components/MonitoringAgent/utils/monitoringSelectionUtils';
import { isMonitoringSessionType } from 'components/MonitoringAgent/utils/monitoringSessionFlowUtils';
import { AI_PANEL_SELECTABLE_UNIT_TYPES } from 'constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';
import {
  getUnitIdFromNodeId,
} from 'utils/AICreatorHelper';
import { getUnitDisplayName } from 'utils/reactFlowHelpers';

import {
  buildAllAvailableAICards,
  buildBoMManufacturerCards,
  selectAICardsFromUnitIds,
  selectCardsFromSession,
} from '../utils/valueChainCardSelectors';

import type { Node } from '@xyflow/react';
import type { IAICardSelection } from '../../../../../../components/AICardPanel/interfaces/IAICardSelection';
import type ISessionVCItem from '../../../../../../store/slices/valueChainMap/interfaces/ISessionVCItem';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type {
  IUseValueChainAICardsParams,
  IUseValueChainAICardsReturn,
} from '../interfaces/IUseValueChainAICards';

const useValueChainAICards = ({
  nodes,
  nodesRef,
  openAICardPanel,
  isUnitSelectionUnlocked = true,
  aiSessionPreviewSelectedUnitIds,
  agentSessions,
  bomUnitsCards,
  supplierUnitsCards,
  selectableUnitTypes = AI_PANEL_SELECTABLE_UNIT_TYPES,
  allowCrossSwimlaneSelection = false,
  monitoringSelectionMode,
  onExpandCountryGroupStack,
  onToggleUnitCollapse,
}: IUseValueChainAICardsParams): IUseValueChainAICardsReturn => {
  const [selectedAICards, setSelectedAICards] = useState<IAICardSelection[]>([]);
  const [generatingAICards, setGeneratingAICards] = useState<IAICardSelection[]>([]);

  const getSelectedAICardsFromUnitIds = useCallback((
    unitIds: number[],
    sourceNodes: Node[] = nodesRef.current,
  ): IAICardSelection[] => selectAICardsFromUnitIds(unitIds, sourceNodes), [nodesRef]);

  const getSelectedCardsFromSession = useCallback((sessionItem?: ISessionVCItem): IAICardSelection[] => (
    selectCardsFromSession(
      sessionItem,
      nodesRef.current,
      [selectedAICards, bomUnitsCards, supplierUnitsCards, generatingAICards],
    )
  ), [
    bomUnitsCards,
    generatingAICards,
    nodesRef,
    selectedAICards,
    supplierUnitsCards,
  ]);

  const isUnitSelectable = useCallback((unitType: string, swimlaneId: string) => {
    if (monitoringSelectionMode) {
      return isSelectableForMonitoringSelectionMode(
        monitoringSelectionMode,
        unitType,
        swimlaneId,
        nodesRef.current,
      );
    }

    return isUnitSelectableForAIPanel(unitType, selectableUnitTypes);
  }, [monitoringSelectionMode, nodesRef, selectableUnitTypes]);

  const onToggleAIPanelCardSelection = useCallback((nodeId: string, nextSelectedState?: boolean) => {
    const selectedNode = nodesRef.current.find((nodeItem) => nodeItem.id === nodeId);
    if (!selectedNode || selectedNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return;
    }

    const nodeData = selectedNode.data as unknown as ICustomUnitNodeData;
    const unitType = String(nodeData.type || '');
    const swimlaneId = String(nodeData.swimlaneId || '');
    const unitName = getUnitDisplayName(nodeData);

    if (!isUnitSelectionUnlocked || !isUnitSelectable(unitType, swimlaneId) || !unitName) {
      return;
    }

    setSelectedAICards((prevCards) => {
      const isAlreadySelected = prevCards.some((card) => card.nodeId === nodeId);
      const shouldSelect = typeof nextSelectedState === 'boolean'
        ? nextSelectedState
        : !isAlreadySelected;

      if (!shouldSelect) {
        if (!isAlreadySelected) {
          return prevCards;
        }
        return prevCards.filter((card) => card.nodeId !== nodeId);
      }

      if (isAlreadySelected) {
        return prevCards;
      }

      if (!allowCrossSwimlaneSelection && prevCards.length > 0) {
        const firstCard = prevCards[0];
        const hasDifferentType = firstCard.type !== unitType;
        const hasDifferentSwimlane = firstCard.swimlaneId !== swimlaneId;

        if (hasDifferentType || hasDifferentSwimlane) {
          return prevCards;
        }
      }

      return [
        ...prevCards,
        {
          nodeId,
          name: unitName,
          type: unitType,
          swimlaneId,
        },
      ];
    });
  }, [allowCrossSwimlaneSelection, isUnitSelectable, isUnitSelectionUnlocked, nodesRef]);

  const applyAIPanelStateToNodes = useCallback((nodesToDecorate: Node[]): Node[] => {
    const lockedSelection = selectedAICards[0];
    let hasChanges = false;

    const nextNodes = nodesToDecorate.map((nodeToDecorate) => {
      if (nodeToDecorate.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        return nodeToDecorate;
      }

      const nodeData = nodeToDecorate.data as unknown as ICustomUnitNodeData;
      const nodeType = String(nodeData.type || '');
      const nodeSwimlaneId = String(nodeData.swimlaneId || '');
      const unitId = getUnitIdFromNodeId(nodeToDecorate.id);
      const isSelectableCard = isUnitSelectable(nodeType, nodeSwimlaneId);
      const isSelected = selectedAICards.some((card) => card.nodeId === nodeToDecorate.id);
      const isSessionPreviewSelected = unitId !== null && aiSessionPreviewSelectedUnitIds.has(unitId);
      const isSelectionEnabled = openAICardPanel
        && isUnitSelectionUnlocked
        && isSelectableCard
        && (
          allowCrossSwimlaneSelection
          || !lockedSelection
          || (
            lockedSelection.type === nodeType
            && lockedSelection.swimlaneId === nodeSwimlaneId
          )
        );

      const hasNoChanges = nodeData.isAIPanelSelectionEnabled === isSelectionEnabled
        && nodeData.isAIPanelSelected === isSelected
        && nodeData.isAISessionPreviewSelected === isSessionPreviewSelected
        && nodeData.isAICardPanelOpen === openAICardPanel
        && nodeData.onExpandCountryGroupStack === onExpandCountryGroupStack
        && nodeData.onToggleUnitCollapse === onToggleUnitCollapse
        && nodeData.onToggleAIPanelCardSelection === onToggleAIPanelCardSelection;

      if (hasNoChanges) {
        return nodeToDecorate;
      }

      hasChanges = true;

      return {
        ...nodeToDecorate,
        data: {
          ...nodeData,
          isAIPanelSelectionEnabled: isSelectionEnabled,
          isAIPanelSelected: isSelected,
          isAISessionPreviewSelected: isSessionPreviewSelected,
          isAICardPanelOpen: openAICardPanel,
          onExpandCountryGroupStack,
          onToggleUnitCollapse,
          onToggleAIPanelCardSelection,
        } as unknown as Record<string, unknown>,
      };
    });

    return hasChanges ? nextNodes : nodesToDecorate;
  }, [
    aiSessionPreviewSelectedUnitIds,
    allowCrossSwimlaneSelection,
    isUnitSelectable,
    isUnitSelectionUnlocked,
    onExpandCountryGroupStack,
    onToggleAIPanelCardSelection,
    onToggleUnitCollapse,
    openAICardPanel,
    selectedAICards,
  ]);

  const allAvailableAICards = useMemo<IAICardSelection[]>(
    () => buildAllAvailableAICards(nodes, selectableUnitTypes, monitoringSelectionMode),
    [monitoringSelectionMode, nodes, selectableUnitTypes],
  );

  const availableBoMManufacturerCards = useMemo<IAICardSelection[]>(
    () => buildBoMManufacturerCards(nodes),
    [nodes],
  );

  const generatingAISessions = useMemo(() => (agentSessions || [])
    .filter((sessionItem) => !isMonitoringSessionType(sessionItem.type))
    .map((sessionItem) => ({
      session: sessionItem,
      selectedCards: getSelectedAICardsFromUnitIds(
        sessionItem.selectedUnits.map(({ unitId }) => unitId),
        nodes,
      ),
    })), [agentSessions, getSelectedAICardsFromUnitIds, nodes]);

  const onSyncSelectedAICardsFromSession = useCallback((unitIds: number[]) => {
    const nextSelectedCards = getSelectedAICardsFromUnitIds(unitIds);

    setSelectedAICards(nextSelectedCards);
  }, [getSelectedAICardsFromUnitIds]);

  const onRemoveSelectedAICard = useCallback((nodeId: string) => {
    setSelectedAICards((prevCards) => prevCards.filter((card) => card.nodeId !== nodeId));
  }, []);

  const onResetSelectedAICards = useCallback(() => {
    setSelectedAICards([]);
  }, []);

  useEffect(() => {
    if (!selectedAICards.length) {
      return;
    }

    const existingNodeIds = new Set(
      nodes
        .filter((nodeItem) => nodeItem.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT)
        .map((nodeItem) => nodeItem.id),
    );

    setSelectedAICards((prevCards) => {
      const nextCards = prevCards.filter((card) => existingNodeIds.has(card.nodeId));
      return nextCards.length === prevCards.length ? prevCards : nextCards;
    });
  }, [nodes, selectedAICards.length]);

  return {
    selectedAICards,
    setSelectedAICards,
    generatingAICards,
    setGeneratingAICards,
    allAvailableAICards,
    availableBoMManufacturerCards,
    generatingAISessions,
    getSelectedAICardsFromUnitIds,
    getSelectedCardsFromSession,
    onToggleAIPanelCardSelection,
    onSyncSelectedAICardsFromSession,
    onRemoveSelectedAICard,
    onResetSelectedAICards,
    applyAIPanelStateToNodes,
  };
};

export default useValueChainAICards;
